import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Users, Eye, EyeOff, Loader2, Download, Smartphone, Apple, X } from 'lucide-react';

// Android icon component
const AndroidIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M17.523 15.341c-.5 0-.91-.41-.91-.91s.41-.91.91-.91.91.41.91.91-.41.91-.91.91m-11.046 0c-.5 0-.91-.41-.91-.91s.41-.91.91-.91.91.41.91.91-.41.91-.91.91m11.405-6.02l1.99-3.46c.11-.19.05-.43-.14-.54-.19-.11-.43-.05-.54.14l-2.02 3.51c-1.51-.69-3.2-1.08-5.02-1.08s-3.51.39-5.02 1.08l-2.02-3.51c-.11-.19-.35-.25-.54-.14-.19.11-.25.35-.14.54l1.99 3.46C2.62 11.07 0 14.61 0 18.71h24c0-4.1-2.62-7.64-6.12-9.39"/>
  </svg>
);

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [telepon, setTelepon] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showIOSModal, setShowIOSModal] = useState(false);
  const [showWAModal, setShowWAModal] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(telepon, password);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Login gagal. Periksa nomor telepon dan password Anda.');
    } finally {
      setLoading(false);
    }
  };

  const handleAndroidDownload = () => {
    // Download APK file
    window.location.href = '/download/silsa.apk';
  };

  const handleIOSInstall = () => {
    setShowIOSModal(true);
  };

  const WA_NUMBER = process.env.REACT_APP_WA_NUMBER;

  const handleOpenWhatsApp = () => {
    const text = encodeURIComponent('REGISTRASI - Mohon kirimkan password login saya');
    window.open(`https://wa.me/${WA_NUMBER}?text=${text}`, '_blank');
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left side - Background */}
      <div 
        className="hidden lg:flex lg:w-1/2 relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #0F62FE 0%, #0043CE 50%, #002D9C 100%)'
        }}
      >
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-72 h-72 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        </div>
        <div className="relative z-10 flex flex-col justify-center px-16 text-white">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-xl overflow-hidden backdrop-blur-sm">
              <img src="/logo192.png" alt="SILSA" className="w-full h-full object-cover" />
            </div>
            <span className="text-3xl font-bold tracking-tight font-['Manrope']">SILSA</span>
          </div>
          <h1 className="text-4xl font-bold mb-4 font-['Manrope'] leading-tight">
            Sistem HR Modern<br />untuk Perusahaan Anda
          </h1>
          <p className="text-lg text-white/80 max-w-md leading-relaxed">
            Kelola karyawan, absensi, cuti, dan payroll dengan mudah dan efisien.
          </p>
          <div className="mt-12 grid grid-cols-3 gap-6">
            <div className="text-center">
              <div className="text-3xl font-bold">100+</div>
              <div className="text-sm text-white/70">Perusahaan</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold">10K+</div>
              <div className="text-sm text-white/70">Karyawan</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold">99%</div>
              <div className="text-sm text-white/70">Kepuasan</div>
            </div>
          </div>

          {/* Download App Section - Desktop */}
          <div className="mt-12 p-6 bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <Smartphone className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-lg">Download Aplikasi</h3>
                <p className="text-sm text-white/70">Install SILSA di HP Anda</p>
              </div>
            </div>
            <div className="flex gap-3">
              <Button 
                onClick={handleAndroidDownload}
                className="flex-1 bg-[#3DDC84] hover:bg-[#32c973] text-black font-medium"
                data-testid="android-download-btn-desktop"
              >
                <AndroidIcon className="w-5 h-5 mr-2" />
                Android APK
              </Button>
              <Button 
                onClick={handleIOSInstall}
                className="flex-1 bg-white hover:bg-gray-100 text-black font-medium"
                data-testid="ios-install-btn-desktop"
              >
                <Apple className="w-5 h-5 mr-2" />
                iOS PWA
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-slate-50">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
            <div className="w-10 h-10 rounded-xl overflow-hidden">
              <img src="/logo192.png" alt="SILSA" className="w-full h-full object-cover" />
            </div>
            <span className="text-2xl font-bold tracking-tight font-['Manrope'] text-foreground">SILSA</span>
          </div>

          <Card className="border-0 shadow-xl bg-white">
            <CardHeader className="space-y-1 pb-6">
              <CardTitle className="text-2xl font-bold text-center font-['Manrope']">
                Selamat Datang
              </CardTitle>
              <CardDescription className="text-center">
                Masuk ke akun Anda untuk melanjutkan
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-5">
                {error && (
                  <Alert variant="destructive" className="animate-fade-in">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <div className="space-y-2">
                  <Label htmlFor="telepon" className="text-sm font-medium">Nomor Telepon</Label>
                  <Input
                    id="telepon"
                    type="text"
                    placeholder="08123456789"
                    value={telepon}
                    onChange={(e) => setTelepon(e.target.value)}
                    required
                    data-testid="login-phone-input"
                    className="h-11"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-sm font-medium">Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Masukkan password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      data-testid="login-password-input"
                      className="h-11 pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full h-11 font-medium"
                  disabled={loading}
                  data-testid="login-submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Memproses...
                    </>
                  ) : (
                    'Masuk'
                  )}
                </Button>
                <button
                  type="button"
                  onClick={() => setShowWAModal(true)}
                  className="w-full text-sm text-primary hover:underline mt-1 py-1"
                  data-testid="kirim-wa-btn"
                >
                  Lupa / belum punya password? Minta lewat WhatsApp
                </button>
              </form>

              {/* Download App Section - Mobile */}
              <div className="mt-6 pt-6 border-t border-slate-100">
                <p className="text-sm font-medium text-center mb-4 text-muted-foreground">
                  📱 Download Aplikasi
                </p>
                <div className="flex gap-3">
                  <Button
                    type="button"
                    onClick={handleAndroidDownload}
                    className="flex-1 h-11 bg-[#3DDC84] hover:bg-[#32c973] text-black font-medium"
                    data-testid="android-download-btn-mobile"
                  >
                    <AndroidIcon className="w-5 h-5 mr-2" />
                    Android
                  </Button>
                  <Button
                    type="button"
                    onClick={handleIOSInstall}
                    variant="outline"
                    className="flex-1 h-11 font-medium border-slate-300"
                    data-testid="ios-install-btn-mobile"
                  >
                    <Apple className="w-5 h-5 mr-2" />
                    iOS
                  </Button>
                </div>
              </div>

            </CardContent>
          </Card>

          <div className="text-center mt-6 space-y-1">
            <a
              href="/panduan"
              className="text-sm text-primary hover:underline"
              data-testid="login-panduan-link"
            >
              Panduan Penggunaan
            </a>
            <p className="text-sm text-muted-foreground">
              &copy; {new Date().getFullYear()} SILSA. All rights reserved.
            </p>
            <p className="text-xs text-muted-foreground/70">
              Powered by <span className="font-medium">PT Lintas Sinergi Andalan</span>
            </p>
          </div>
        </div>
      </div>


      {/* WA Kredensial Modal */}
      <Dialog open={showWAModal} onOpenChange={setShowWAModal}>
        <DialogContent className="max-w-sm" data-testid="wa-kredensial-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Smartphone className="w-5 h-5 text-green-600" />
              Minta Password via WhatsApp
            </DialogTitle>
            <DialogDescription>
              Nomor WhatsApp Anda harus sudah terdaftar di sistem oleh admin HR
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <ol className="text-sm text-muted-foreground space-y-2 list-decimal list-inside">
              <li>Tap tombol di bawah untuk membuka WhatsApp</li>
              <li>Kirim pesan yang sudah otomatis terisi</li>
              <li>Password login akan dibalas otomatis ke WhatsApp Anda dalam beberapa detik</li>
            </ol>
            <Button
              onClick={handleOpenWhatsApp}
              className="w-full h-11 bg-green-600 hover:bg-green-700 text-white"
              data-testid="open-whatsapp-btn"
            >
              <Smartphone className="w-4 h-4 mr-2" />
              Buka WhatsApp
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      {/* iOS PWA Installation Modal */}
      <Dialog open={showIOSModal} onOpenChange={setShowIOSModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Apple className="w-6 h-6" />
              Install di iPhone/iPad
            </DialogTitle>
            <DialogDescription>
              Ikuti langkah berikut untuk menginstall SILSA
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="flex gap-4 items-start">
              <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center text-sm font-bold shrink-0">
                1
              </div>
              <div>
                <p className="font-medium">Buka di Safari</p>
                <p className="text-sm text-muted-foreground">Pastikan Anda membuka hr.silsa.id menggunakan browser Safari</p>
              </div>
            </div>
            
            <div className="flex gap-4 items-start">
              <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center text-sm font-bold shrink-0">
                2
              </div>
              <div>
                <p className="font-medium">Tap tombol Share</p>
                <p className="text-sm text-muted-foreground">Ketuk ikon <span className="inline-flex items-center px-1 py-0.5 bg-slate-100 rounded text-xs">⬆️ Share</span> di bagian bawah layar</p>
              </div>
            </div>
            
            <div className="flex gap-4 items-start">
              <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center text-sm font-bold shrink-0">
                3
              </div>
              <div>
                <p className="font-medium">Add to Home Screen</p>
                <p className="text-sm text-muted-foreground">Scroll dan pilih <span className="inline-flex items-center px-1 py-0.5 bg-slate-100 rounded text-xs">➕ Add to Home Screen</span></p>
              </div>
            </div>
            
            <div className="flex gap-4 items-start">
              <div className="w-8 h-8 rounded-full bg-green-500 text-white flex items-center justify-center text-sm font-bold shrink-0">
                ✓
              </div>
              <div>
                <p className="font-medium">Selesai!</p>
                <p className="text-sm text-muted-foreground">SILSA akan muncul di home screen seperti aplikasi native</p>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 p-3 rounded-lg">
            <p className="text-xs text-blue-700">
              <strong>Tips:</strong> Setelah install, Anda bisa langsung absensi dari home screen tanpa perlu buka browser lagi.
            </p>
          </div>

          <Button onClick={() => setShowIOSModal(false)} className="w-full mt-2">
            Mengerti
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LoginPage;
